import z from 'zod/v4';
import type { DwReportKey } from '@/lib/datawarehouse/reports/registry';
import { TAG_USAGE_PERIODS } from '@/lib/datawarehouse/reports/spaces-filter-parts';
import { DefaultPresetSchema } from './default-preset-schema';
import type { PresetConfig } from './types';
import { createPresetSchema } from './utils';

/**
 * A saved setup of a datawarehouse report (Rapportmall, see GLOSSARY.md and
 * docs/adr/0001). Ids only — names are looked up again when a template
 * opens. Dates are stored as `ReportDate` / `ReportDateRange`, so a dynamic
 * date stays dynamic. A new filter means a version bump plus a migration
 * that fills in its default.
 */

const IdsSchema = z.array(z.string());

export const ReportDateSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fixed'), date: z.iso.date() }),
  z.object({
    kind: z.literal('relative'),
    preset: z.enum(['today', 'startOfMonth', 'endOfMonth', 'threeMonthsAgo']),
  }),
]);

export const ReportDateRangeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('preset'),
    preset: z.enum(['thisMonth', 'lastMonth', 'last3Months', 'thisYear']),
  }),
  z.object({
    kind: z.literal('custom'),
    from: ReportDateSchema.nullable(),
    to: ReportDateSchema.nullable(),
  }),
]);

/**
 * The shared filters of the spaces reports (objects, rentroll, vacancies).
 * Fastigheter are not here — they live in `realestateSelection`.
 */
const SpacesFiltersSchema = z.object({
  types: z.array(z.string()),
  companyIds: IdsSchema,
  communityIds: IdsSchema,
  regionIds: IdsSchema,
  structureIds: IdsSchema,
  tenantIds: IdsSchema,
  /** address.city values — a city's name is its id. */
  cities: IdsSchema,
  /** space.category codes. */
  categories: IdsSchema,
  roomCounts: z.array(z.number()),
  /** null = unset. */
  areaMin: z.number().nullable(),
  areaMax: z.number().nullable(),
  floors: z.array(z.number()),
  tagIds: IdsSchema,
  tagPeriods: z.array(z.enum(TAG_USAGE_PERIODS)).min(1),
});

const SpacesStatusSchema = z.enum(['all', 'leased', 'vacant']);
/** Months; null = off. */
const ContractEndWithinSchema = z.number().int().positive().nullable();

const ObjectsFiltersSchema = z.object({
  spaces: SpacesFiltersSchema,
  status: SpacesStatusSchema,
  contractEndWithin: ContractEndWithinSchema,
});

const RentRollFiltersSchema = z.object({
  spaces: SpacesFiltersSchema,
  asOf: ReportDateSchema,
  status: SpacesStatusSchema,
  contractEndWithin: ContractEndWithinSchema,
  subContract: z.enum(['all', 'yes', 'no']),
});

const VacanciesFiltersSchema = z.object({
  spaces: SpacesFiltersSchema,
  asOf: ReportDateSchema,
  /** null = unset. */
  minVacancyDays: z.number().nullable(),
  maxVacancyDays: z.number().nullable(),
  vacant: ReportDateRangeSchema,
  /** null = Alla. */
  hasNextContract: z.boolean().nullable(),
  onlyNotedVacancies: z.boolean(),
});

const ErrandsFiltersSchema = z.object({
  created: ReportDateRangeSchema,
  statuses: z.array(z.number().int()),
  priorities: z.array(z.number().int()),
  companyIds: IdsSchema,
  structureIds: IdsSchema,
  errandTypeIds: IdsSchema,
  errandSubTypeIds: IdsSchema,
  reportingChannels: z.array(z.number().int()),
  performerIds: IdsSchema,
  reporterTypes: z.array(z.string()),
  objectTypes: z.array(z.string()),
  tagIds: IdsSchema,
  olderThanDays: z.number().int().positive().nullable(),
  reported: ReportDateRangeSchema,
  onlyWarranty: z.boolean(),
  onlyOverdue: z.boolean(),
  onlyUnassigned: z.boolean(),
});

const InvoicesFiltersSchema = z.object({
  period: ReportDateRangeSchema,
  companyIds: IdsSchema,
  invoiceKind: z.enum(['all', 'rental', 'non_rental']),
  includeUnattested: z.boolean(),
  openClosed: z.enum(['all', 'open', 'closed']),
  internal: z.enum(['all', 'yes', 'no']),
});

export const RealestateSelectionSchema = z.object({
  segmentIds: IdsSchema,
  realestateIds: IdsSchema,
});

const CommonSchema = z.object({
  title: z.string().trim().min(1),
  /** null = shared with everyone; only meaningful on a public template. */
  workEngagementIds: IdsSchema.nullable(),
  /** null = not saved: the report starts from the global filter. */
  realestateSelection: RealestateSelectionSchema.nullable(),
  sort: z
    .object({
      key: z.string(),
      path: z.string(),
      direction: z.enum(['asc', 'desc']),
    })
    .nullable(),
  /** Picker keys, as in the report table's column prefs. */
  columns: z.object({
    visibility: z.record(z.string(), z.boolean()),
    order: IdsSchema,
  }),
});

const V1DataSchema = z.discriminatedUnion('reportKey', [
  z.object({
    ...CommonSchema.shape,
    reportKey: z.literal('objects' satisfies DwReportKey),
    filters: ObjectsFiltersSchema,
  }),
  z.object({
    ...CommonSchema.shape,
    reportKey: z.literal('rentroll' satisfies DwReportKey),
    filters: RentRollFiltersSchema,
  }),
  z.object({
    ...CommonSchema.shape,
    reportKey: z.literal('vacancies' satisfies DwReportKey),
    filters: VacanciesFiltersSchema,
  }),
  z.object({
    ...CommonSchema.shape,
    reportKey: z.literal('errands' satisfies DwReportKey),
    filters: ErrandsFiltersSchema,
  }),
  z.object({
    ...CommonSchema.shape,
    reportKey: z.literal('invoices' satisfies DwReportKey),
    filters: InvoicesFiltersSchema,
  }),
]);

export const V1Schema = createPresetSchema('1', V1DataSchema);

export const DW_REPORT_TEMPLATE_PRESET_IDENTIFIER = 'dw.report-template';

const DwReportTemplatePresetSchema = z.object({
  ...DefaultPresetSchema.shape,
  context: z.literal(DW_REPORT_TEMPLATE_PRESET_IDENTIFIER),
  data: V1Schema,
});

export const DwReportTemplatePreset = {
  identifier: DW_REPORT_TEMPLATE_PRESET_IDENTIFIER,
  currentSchema: DwReportTemplatePresetSchema,
  currentVersion: '1',
  migrations: [],
  single: false,
} as const satisfies PresetConfig;

export type DwReportTemplatePreset = z.infer<
  typeof DwReportTemplatePresetSchema
>;
export type DwReportTemplateData = DwReportTemplatePreset['data']['presetData'];
export type DwReportTemplateDataFor<K extends DwReportKey> = Extract<
  DwReportTemplateData,
  { reportKey: K }
>;
export type RealestateSelection = z.infer<typeof RealestateSelectionSchema>;

/**
 * pigello-ui-settings caps every data string at 10 000 characters
 * (`MAX_DATA_STRING_VALUE_LENGTH`) — roughly 250 fastighet ids.
 */
export const DW_REPORT_TEMPLATE_MAX_DATA_LENGTH = 10_000;

/** Whether the template fits, serialised the way use-mutate-preset.ts sends it. */
export function fitsDwReportTemplateSizeLimit(
  presetData: DwReportTemplateData
): boolean {
  return JSON.stringify(presetData).length < DW_REPORT_TEMPLATE_MAX_DATA_LENGTH;
}
